import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Crown, Lightning, Gift, ArrowUp, XCircle, ShieldCheck } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_META = {
    free: { name: 'Free', color: 'text-muted-foreground', bg: 'bg-white/5', border: 'border-white/10', icon: Lightning, desc: 'Basic access — track rides, browse, and earn at 1× rate.' },
    standard: { name: 'Standard', color: 'text-primary', bg: 'bg-primary/5', border: 'border-primary/30', icon: Lightning, desc: '2× Bolt earn rate, premium challenges, priority leaderboard, monthly Bolt bonus, and early access.' },
    pro: { name: 'Pro', color: 'text-boltz-gold', bg: 'bg-boltz-gold/5', border: 'border-boltz-gold/30', icon: Crown, desc: 'Coming soon.' },
    elite: { name: 'Elite', color: 'text-hot-pink', bg: 'bg-hot-pink/5', border: 'border-hot-pink/30', icon: Crown, desc: 'Coming soon.' },
    vip: { name: 'VIP', color: 'text-boltz-gold', bg: 'bg-boltz-gold/5', border: 'border-boltz-gold/30', icon: Crown, desc: 'Full access.' },
    admin: { name: 'Admin', color: 'text-neon-green', bg: 'bg-neon-green/5', border: 'border-neon-green/30', icon: ShieldCheck, desc: 'Full unrestricted access to every feature — no payment required.' },
};

export default function Billing() {
    const { updateUser } = useAuth();
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');

    const load = useCallback(async () => {
        try {
            const r = await fetch(`${API}/subscription/me`, { credentials: 'include' });
            const d = await r.json();
            setSub(d);
        } catch (e) { console.warn('Billing load:', e.message); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const upgrade = async () => {
        setBusy('upgrade');
        try {
            const r = await fetch(`${API}/subscription/checkout`, {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tier: 'standard', origin_url: window.location.origin }),
            });
            const d = await r.json();
            if (r.ok && d.checkout_url) { window.location.href = d.checkout_url; }
            else { toast.error(d.detail || 'Could not start checkout'); setBusy(''); }
        } catch (e) { toast.error('Network error'); setBusy(''); }
    };

    const cancel = async () => {
        setBusy('cancel');
        try {
            const r = await fetch(`${API}/subscription/cancel`, { method: 'POST', credentials: 'include' });
            const d = await r.json();
            if (r.ok) { toast.success('Subscription cancelled — you are now on Free'); await load(); }
            else toast.error(d.detail || 'Cancel failed');
        } catch (e) { toast.error('Network error'); }
        finally { setBusy(''); }
    };

    const claimBonus = async () => {
        setBusy('bonus');
        try {
            const r = await fetch(`${API}/subscription/claim-bonus`, { method: 'POST', credentials: 'include' });
            const d = await r.json();
            if (r.ok) { toast.success(`Claimed ${d.bonus} Boltz monthly bonus!`); updateUser({ boltz_balance: d.boltz_balance }); }
            else toast.error(d.detail || 'Could not claim bonus');
        } catch (e) { toast.error('Network error'); }
        finally { setBusy(''); }
    };

    const tier = sub?.tier || 'free';
    const meta = TIER_META[tier] || TIER_META.free;
    const Icon = meta.icon;
    const isAdmin = !!sub?.admin;
    const isPremium = !!sub?.premium;
    const isFree = tier === 'free';

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="billing-page">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-boltz-gold text-glow-gold">MEMBERSHIP</span>
                    <h1 className="text-2xl sm:text-3xl font-black mt-1 font-display" data-testid="billing-title">Manage Billing</h1>
                    <p className="text-muted-foreground text-sm mt-1">View your plan, switch tiers, and claim your monthly Bolt bonus.</p>
                </motion.div>

                {loading ? (
                    <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />
                ) : (
                    <>
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                            <Card className={`overflow-hidden border ${meta.border} ${meta.bg}`} data-testid="billing-plan-card">
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-12 h-12 rounded-xl ${meta.bg} border ${meta.border} flex items-center justify-center`}>
                                                <Icon size={22} className={meta.color} weight="fill" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Current Plan</p>
                                                <h2 className={`text-xl font-display font-bold ${meta.color}`} data-testid="billing-plan-name">{meta.name}</h2>
                                            </div>
                                        </div>
                                        <Badge className={`${meta.bg} ${meta.color} border ${meta.border} text-[11px]`} data-testid="billing-status">
                                            {isAdmin ? 'Full Access' : isPremium ? 'Active' : 'Free'}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-4">{meta.desc}</p>
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Actions */}
                        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {isFree && !isAdmin && (
                                <Button onClick={upgrade} disabled={busy === 'upgrade'}
                                    className="rounded-full font-display bg-primary text-black hover:bg-primary/80"
                                    data-testid="billing-upgrade-btn">
                                    <ArrowUp size={16} className="mr-1.5" weight="bold" />
                                    {busy === 'upgrade' ? 'Redirecting…' : 'Upgrade to Standard · $9.99/mo'}
                                </Button>
                            )}

                            {(isPremium || isAdmin) && (
                                <Button onClick={claimBonus} disabled={busy === 'bonus'}
                                    className="rounded-full font-display bg-boltz-gold text-black hover:bg-boltz-gold/80"
                                    data-testid="billing-claim-bonus-btn">
                                    <Gift size={16} className="mr-1.5" weight="fill" />
                                    {busy === 'bonus' ? 'Claiming…' : 'Claim Monthly Bolt Bonus'}
                                </Button>
                            )}

                            {isPremium && !isAdmin && (
                                <Button onClick={cancel} disabled={busy === 'cancel'} variant="outline"
                                    className="rounded-full font-display border-destructive/30 text-destructive hover:bg-destructive/10"
                                    data-testid="billing-cancel-btn">
                                    <XCircle size={16} className="mr-1.5" weight="fill" />
                                    {busy === 'cancel' ? 'Cancelling…' : 'Cancel Subscription'}
                                </Button>
                            )}
                        </div>

                        {!isAdmin && (
                            <p className="text-[11px] text-muted-foreground mt-6">
                                {isPremium
                                    ? 'Billed monthly via Stripe. Cancelling reverts you to Free at any time.'
                                    : 'Upgrade unlocks 2× Bolt earning, premium challenges, priority leaderboard, and a monthly Bolt bonus.'}
                            </p>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}
